/**
 * AccountInfoSection — N2 (Ablauf-Kennzeichnung in /settings)
 *
 * Sidebar-Karte "Account summary" (Design-Referenz settings.html Profil-Tab,
 * rechte Spalte): Plan, Laufzeitende, Mitglied-seit, Kontingent-Zaehler und
 * Link zur Abo-Verwaltung. E-Mail/Waehrung/Zeitzone liegen in der linken
 * "Account"-Karte (siehe AccountFormSection) — Aufsplittung folgt exakt dem
 * Referenz-Layout (eine Karte links, eine Zusammenfassung rechts).
 *
 * Aus routes/_auth.settings.tsx extrahiert, damit der ECHTE Datenfluss
 * (usePremiumPlan -> useSubscription -> GET /api/v2/subscription) testbar ist,
 * ohne die Route mit Router-Kontext rendern zu muessen.
 *
 * Kernregel: Ein abgelaufenes Abo (subscription.status === 'expired') darf nie
 * als neutrales "Premium bis: <Datum>" erscheinen — Label, Farbe und Plan-Badge
 * kennzeichnen den Ablauf explizit.
 */
import { useTranslation } from 'react-i18next'
import { Link } from '@tanstack/react-router'
import { formatLocalDateWithPattern } from '../../lib/locale'
import { planDisplayName } from '../premium/planDisplayName'
import { usePremiumPlan } from '../premium/usePremiumPlan'
import type { AccountData } from './useAccount'

/**
 * #124 (= #55, voller Fix): Settings > Display speichert `date_format`
 * (Werte aus routes/_auth.settings.tsx DATE_FORMATS, z.B. "DD.MM.YYYY").
 * Der vorherige Teilfix bildete das Pattern lokal in dieser Karte auf
 * date-fns-Tokens ab — dupliziert jetzt nicht mehr die Pattern-Map, sondern
 * delegiert an die zentrale Ableitung aus lib/locale.ts. Nutzt bewusst
 * `formatLocalDateWithPattern(value, account.date_format)` statt des
 * Modul-State-Helpers `formatLocalDate` — `account` liegt hier bereits als
 * Prop vor (siehe Datei-Kommentar oben: Komponente ist unabhängig von
 * Router-Kontext testbar), das Ergebnis soll also unabhängig davon
 * korrekt sein, ob useAccount(), das den Modul-State über
 * setDisplayPreferences befüllt, in diesem Rendering-Pfad bereits gelaufen
 * ist.
 */
function formatDate(
  value: string | null | undefined,
  dateFormat: string | null | undefined,
): string {
  return formatLocalDateWithPattern(value, dateFormat)
}

export function AccountInfoSection({ account }: { account: AccountData }) {
  const { t } = useTranslation('settings')
  // `plan` ist bei abgelaufenem Abo bewusst null — expiredPlanName traegt dann den Namen.
  const { isExpired, planName } = usePremiumPlan()

  return (
    <div className="card card-pad" data-testid="account-section">
      <h3 style={{ marginBottom: 14 }}>{t('summary.title')}</h3>
      {/*
        Referenz-Layout (settings.html): reiner Fliesstext ("Plan Ideal ·
        renews 2026-09-14"), KEINE umrandeten Input-Boxen — die sehen wie
        deaktivierte Formularfelder aus, sind es aber nicht (P1-Fund).
      */}
      <div className="muted" style={{ fontSize: 13, lineHeight: 1.8, marginBottom: 14 }}>
        <div>
          <span>{t('account.plan')}</span>{' '}
          <span
            className={isExpired ? 'pill pill-danger' : 'pill pill-primary'}
            data-testid="settings-plan-badge"
          >
            {planName ?? planDisplayName(account.plan) ?? account.plan}
            {isExpired ? ` · ${t('account.premiumExpiredBadge')}` : ''}
          </span>
        </div>
        <div
          style={{ color: isExpired ? 'var(--danger)' : undefined }}
          data-testid={isExpired ? 'settings-premium-expired' : 'settings-premium-until'}
        >
          <span>{isExpired ? t('account.premiumExpiredLabel') : t('account.premiumUntil')}</span>{' '}
          {formatDate(account.premium_until, account.date_format)}
        </div>
        <div>
          <span>{t('account.memberSince')}</span>{' '}
          {formatDate(account.created_at, account.date_format)}
        </div>
      </div>

      {isExpired && (
        <div
          role="alert"
          className="alert is-warning"
          data-testid="settings-premium-expired-hint"
          style={{ marginBottom: 14, fontSize: 13, color: 'var(--danger)' }}
        >
          {t('account.premiumExpiredHint')}
        </div>
      )}

      <div className="muted" style={{ fontSize: 13, lineHeight: 1.7 }}>
        <div>
          {t('summary.hotspotsCount', { count: account.hotspots_count })}
          {' · '}
          {t('summary.walletsCount', { count: account.wallets_count })}
        </div>
        <div>
          {/* Issue #56: contacts_count + hosts_count zaehlt dieselbe Person
              doppelt — people_count kommt bereits gemergt vom Backend
              (PeopleMergeService, gleiche Quelle wie /api/v2/people). */}
          {t('summary.peopleCount', { count: account.people_count ?? 0 })}
        </div>
      </div>

      <div style={{ marginTop: 10 }}>
        <Link to="/account/subscription" style={{ color: 'var(--primary)', fontSize: 13 }}>
          {t('summary.manageSubscription')} →
        </Link>
      </div>
    </div>
  )
}
